<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import { AdminEmptyState } from '@xjlv2/admin-layout'
import { RrButton, RrModal, RrSpin, RrTable, type TableColumnType } from '@xjlv2/ui'
import type { SaasMaterial } from '../../types'
import { createReferencesRequestController, formatDateTime } from './materialCenterUtils'

interface MaterialReferenceItem {
  id: number
  refModuleCode: string
  refType: string
  refId: string
  createdAt: string
}

const columns: TableColumnType<MaterialReferenceItem>[] = [
  { title: '引用模块', dataIndex: 'refModuleCode', key: 'refModuleCode', width: 140 },
  { title: '业务类型', dataIndex: 'refType', key: 'refType', width: 130 },
  { title: '业务对象 ID', dataIndex: 'refId', key: 'refId', minWidth: 160 },
  { title: '关联时间', dataIndex: 'createdAt', key: 'createdAt', width: 170 }
]

const props = defineProps<{
  open: boolean
  material: SaasMaterial | null
  createApiClient: () => ApiClient
}>()

const emit = defineEmits<{
  'update:open': [val: boolean]
  error: [message: string]
  'after-close': []
}>()

const referencesList = ref<MaterialReferenceItem[]>([])
const loadingReferences = ref(false)
const referencesError = ref('')
const referencesController = createReferencesRequestController()

async function loadReferences(material: SaasMaterial) {
  const requestId = referencesController.open(material.id)
  referencesList.value = []
  referencesError.value = ''
  loadingReferences.value = true
  try {
    const api = props.createApiClient()
    const result = await api.get<MaterialReferenceItem[]>(
      `/api/saas/system/materials/${material.id}/references`
    )
    if (!referencesController.isCurrent(requestId, material.id) || !props.open) return
    referencesList.value = result
  } catch (err) {
    if (!referencesController.isCurrent(requestId, material.id) || !props.open) return
    const message = err instanceof Error ? err.message : '获取素材引用明细失败'
    referencesError.value = message
    emit('error', message)
  } finally {
    if (referencesController.isCurrent(requestId, material.id)) {
      loadingReferences.value = false
    }
  }
}

watch([() => props.open, () => props.material], ([isOpen, material]) => {
  if (isOpen && material) {
    void loadReferences(material)
  } else if (!isOpen) {
    referencesController.close()
    referencesList.value = []
    referencesError.value = ''
  }
})

onUnmounted(() => {
  referencesController.close()
})

function handleRetry() {
  if (props.material) {
    void loadReferences(props.material)
  }
}
</script>

<template>
  <RrModal
    :open="open"
    :title="`引用来源明细 · ${material?.name || ''}`"
    :width="680"
    :footer="null"
    @update:open="(val) => emit('update:open', val)"
    @after-close="emit('after-close')"
  >
    <div class="references-modal-body">
      <div v-if="loadingReferences" class="references-state-box">
        <RrSpin tip="正在加载引用明细..." />
      </div>

      <div v-else-if="referencesError" class="references-error-banner">
        <span>加载引用明细失败（{{ referencesError }}），请勿直接判定为无引用。</span>
        <RrButton size="small" class="retry-btn" @click="handleRetry">重试</RrButton>
      </div>

      <div v-else-if="referencesList.length === 0" class="references-empty-box">
        <AdminEmptyState
          title="暂无外部引用"
          description="当前素材未被任何外部业务产品、装修或营销活动引用，可安全归档或删除。"
        />
      </div>

      <div v-else class="table-container">
        <RrTable
          :columns="columns"
          :data-source="referencesList"
          row-key="id"
          :pagination="false"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'refModuleCode'">
              <strong>{{ (record as MaterialReferenceItem).refModuleCode }}</strong>
            </template>
            <template v-else-if="column.key === 'refId'">
              <code>{{ (record as MaterialReferenceItem).refId }}</code>
            </template>
            <template v-else-if="column.key === 'createdAt'">
              <span class="time-col">{{ formatDateTime((record as MaterialReferenceItem).createdAt) }}</span>
            </template>
          </template>
        </RrTable>
      </div>
    </div>
  </RrModal>
</template>

<style scoped>
.references-modal-body {
  padding: 8px 0;
}

.references-state-box {
  padding: 32px 0;
  text-align: center;
}

.loading-text {
  color: var(--rr-color-text-muted);
  font-size: 14px;
}

.references-error-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
  font-size: 13px;
  border: 1px solid var(--rr-color-danger-border);
}

.retry-btn {
  white-space: nowrap;
  font-size: 12px;
  padding: 4px 10px;
}

.references-empty-box {
  padding: 16px 0;
}

.table-container {
  overflow-x: auto;
}

.table-container strong {
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.table-container code {
  font-family: monospace;
  font-size: 12px;
  background: var(--rr-color-canvas);
  padding: 2px 6px;
  border-radius: 4px;
  color: var(--rr-color-text);
}

.time-col {
  color: var(--rr-color-text-muted);
  font-variant-numeric: tabular-nums;
}
</style>
